import React, { forwardRef, useEffect, useImperativeHandle, useMemo, useRef } from 'react';

export const ClientsTableScrollContext = React.createContext(null);

const EDGE_PX = 16;
const HIDE_MS = 700;
const MIN_THUMB = 28;

const ClientsTableOverlayScroll = forwardRef(({ className = 'clients-table__body', overlay = null, children }, ref) => {
    const hostRef = useRef(null);
    const scrollerRef = useRef(null);
    const scrollContext = useMemo(() => ({ hostRef, scrollerRef }), []);
    const trackRef = useRef(null);
    const thumbRef = useRef(null);

    useImperativeHandle(ref, () => ({
        scrollToBottom() {
            const scroller = scrollerRef.current;
            if (!scroller) {
                return;
            }
            scroller.scrollTop = scroller.scrollHeight;
        },
    }), []);

    useEffect(() => {
        const host = hostRef.current;
        const scroller = scrollerRef.current;
        const track = trackRef.current;
        const thumb = thumbRef.current;
        if (!host || !scroller || !track || !thumb) return undefined;

        let hideTimer = null;
        let hoveringEdge = false;
        let dragging = false;
        let dragOffset = 0;
        const dragClass = 'is-clients-table-scrollbar-drag';

        const canScroll = () => scroller.scrollHeight > scroller.clientHeight + 1;

        const applyThumb = () => {
            if (!canScroll()) {
                track.classList.remove('is-visible');
                return;
            }

            const trackHeight = track.clientHeight;
            const thumbHeight = Math.max(MIN_THUMB, (scroller.clientHeight / scroller.scrollHeight) * trackHeight);
            const maxTop = Math.max(trackHeight - thumbHeight, 0);
            const maxScroll = scroller.scrollHeight - scroller.clientHeight;
            const thumbTop = maxScroll > 0 ? (scroller.scrollTop / maxScroll) * maxTop : 0;
            thumb.style.height = `${thumbHeight}px`;
            thumb.style.transform = `translateY(${thumbTop}px)`;
        };

        const setVisible = (visible) => {
            track.classList.toggle('is-visible', visible && canScroll());
        };

        const show = () => {
            if (hideTimer) {
                clearTimeout(hideTimer);
                hideTimer = null;
            }
            setVisible(true);
        };

        const scheduleHide = () => {
            if (dragging) return;
            if (hideTimer) clearTimeout(hideTimer);
            hideTimer = setTimeout(() => {
                hideTimer = null;
                if (!hoveringEdge && !dragging) setVisible(false);
            }, HIDE_MS);
        };

        const scrollFromClientY = (clientY) => {
            const trackHeight = track.clientHeight;
            const thumbHeight = thumb.offsetHeight;
            const maxTop = Math.max(trackHeight - thumbHeight, 0);
            const nextTop = Math.min(maxTop, Math.max(0, clientY - track.getBoundingClientRect().top - dragOffset));
            const maxScroll = scroller.scrollHeight - scroller.clientHeight;
            scroller.scrollTop = maxTop > 0 ? (nextTop / maxTop) * maxScroll : 0;
        };

        const endDrag = () => {
            if (!dragging) return;
            dragging = false;
            document.documentElement.classList.remove(dragClass);
            scheduleHide();
        };

        const onPointerDown = (event) => {
            if (event.button !== 0 || !canScroll()) return;
            event.preventDefault();
            event.stopPropagation();
            const thumbRect = thumb.getBoundingClientRect();
            const onThumb = event.clientY >= thumbRect.top && event.clientY <= thumbRect.bottom;
            dragOffset = onThumb ? event.clientY - thumbRect.top : thumbRect.height / 2;
            dragging = true;
            show();
            document.documentElement.classList.add(dragClass);
            scrollFromClientY(event.clientY);
            track.setPointerCapture(event.pointerId);
        };

        const onPointerMove = (event) => {
            if (!dragging) return;
            event.preventDefault();
            scrollFromClientY(event.clientY);
        };

        const onScroll = () => {
            applyThumb();
            if (!canScroll()) return;
            show();
            scheduleHide();
        };

        const onMouseMove = (event) => {
            const rect = scroller.getBoundingClientRect();
            const nearEdge = event.clientX >= rect.right - EDGE_PX && event.clientX <= rect.right + 4;
            if (nearEdge === hoveringEdge) return;
            hoveringEdge = nearEdge;
            if (nearEdge) show();
            else scheduleHide();
        };

        const onMouseLeave = () => {
            hoveringEdge = false;
            if (!dragging) scheduleHide();
        };

        scroller.addEventListener('scroll', onScroll, { passive: true });
        host.addEventListener('mousemove', onMouseMove);
        host.addEventListener('mouseleave', onMouseLeave);
        track.addEventListener('pointerdown', onPointerDown);
        track.addEventListener('pointermove', onPointerMove);
        track.addEventListener('pointerup', endDrag);
        track.addEventListener('pointercancel', endDrag);

        const resizeObserver = new ResizeObserver(applyThumb);
        resizeObserver.observe(scroller);
        const mutationObserver = new MutationObserver(applyThumb);
        mutationObserver.observe(scroller, { childList: true, subtree: true });
        applyThumb();

        return () => {
            if (hideTimer) clearTimeout(hideTimer);
            dragging = false;
            document.documentElement.classList.remove(dragClass);
            scroller.removeEventListener('scroll', onScroll);
            host.removeEventListener('mousemove', onMouseMove);
            host.removeEventListener('mouseleave', onMouseLeave);
            track.removeEventListener('pointerdown', onPointerDown);
            track.removeEventListener('pointermove', onPointerMove);
            track.removeEventListener('pointerup', endDrag);
            track.removeEventListener('pointercancel', endDrag);
            resizeObserver.disconnect();
            mutationObserver.disconnect();
        };
    }, []);

    return (
        <ClientsTableScrollContext.Provider value={scrollContext}>
            <div className="clients-table__scroll" ref={hostRef}>
                <div className={className} ref={scrollerRef}>
                    {children}
                </div>
                {overlay}
                <div className="clients-table__scrollbar" ref={trackRef} aria-hidden="true">
                    <div className="clients-table__scrollbar-thumb" ref={thumbRef} />
                </div>
            </div>
        </ClientsTableScrollContext.Provider>
    );
});

export default ClientsTableOverlayScroll;
